import type { ReactElement } from 'react';
import { matchRoutes } from 'react-router-dom';
import { BuscarView } from '../../buscar/views/screens/BuscarView.js';
import { BUSCAR_ROUTE_KEY, buscarLoader } from '../../buscar/buscar.loader.js';
import { CategoriaView } from '../../categoria/views/screens/CategoriaView.js';
import { CATEGORIA_ROUTE_KEY, categoriaLoader } from '../../categoria/categoria.loader.js';
import { HomeView } from '../../home/views/screens/HomeView.js';
import { HOME_ROUTE_KEY, homeLoader } from '../../home/home.loader.js';
import { NotaView } from '../../nota/views/screens/NotaView.js';
import { NOTA_ROUTE_KEY, notaLoader } from '../../nota/nota.loader.js';
import {
  SECCION_VACIA,
  SeccionView,
} from '../../seccion/views/screens/SeccionView.js';
import { SECCION_KIND } from '../../seccion/models/entities/SeccionData.js';
import {
  MAS_LEIDAS_ROUTE_KEY,
  ULTIMA_HORA_ROUTE_KEY,
  masLeidasLoader,
  ultimaHoraLoader,
} from '../../seccion/seccion.loader.js';
import type { RouteLoader } from './RouteLoader.js';

/**
 * Registro **único** de rutas públicas: lo consume el router de React (`App.tsx`) y el
 * server SSR (`entry-server.tsx`). Una sola fuente para que no puedan divergir el path
 * que renderiza el cliente y el que el server usa para elegir el loader.
 *
 * Agregar una página = una entrada acá (path + key + loader + element).
 *
 * Orden y colisiones: `/:categoria` (un segmento) va SIEMPRE al final — el ranking de
 * `matchRoutes` ya prefiere segmentos estáticos (`/buscar`) y patrones más largos
 * (`/:year/:month/:urlKey`), pero cualquier ruta estática de un segmento que se agregue en
 * el futuro tiene que registrarse antes que ella.
 *
 * **Dos rutas que comparten componente no se remontan** al saltar de una a la otra: React reconcilia
 * por tipo, y el `key` del `<Route>` no llega al elemento renderizado. La vista tiene que enterarse
 * del cambio por sus props (ver `SeccionView`, que se registra dos veces). Vale igual para dos URLs
 * del mismo patrón, como `/:categoria`.
 */
export interface SsrRoute {
  /** Patrón de react-router (ej: `/:seccion/:urlKey`). */
  path: string;
  /** Key con la que viaja el estado del loader al cliente. */
  key: string;
  loader: RouteLoader;
  element: ReactElement;
}

export const ssrRoutes: SsrRoute[] = [
  { path: '/', key: HOME_ROUTE_KEY, loader: homeLoader, element: <HomeView /> },
  { path: '/buscar', key: BUSCAR_ROUTE_KEY, loader: buscarLoader, element: <BuscarView /> },
  // Las dos secciones que no son categorías. Sus `urlKey` están en `URL_KEYS_RESERVADAS`, así que
  // ninguna categoría puede quedar tapada por ellas — la API rechaza el alta.
  {
    path: '/ultima-hora',
    key: ULTIMA_HORA_ROUTE_KEY,
    loader: ultimaHoraLoader,
    element: (
      <SeccionView
        kind={SECCION_KIND.ultimaHora}
        routeKey={ULTIMA_HORA_ROUTE_KEY}
        vacio={SECCION_VACIA[SECCION_KIND.ultimaHora]}
      />
    ),
  },
  {
    path: '/mas-leidas',
    key: MAS_LEIDAS_ROUTE_KEY,
    loader: masLeidasLoader,
    element: (
      <SeccionView
        kind={SECCION_KIND.masLeidas}
        routeKey={MAS_LEIDAS_ROUTE_KEY}
        vacio={SECCION_VACIA[SECCION_KIND.masLeidas]}
      />
    ),
  },
  { path: '/:year/:month/:urlKey', key: NOTA_ROUTE_KEY, loader: notaLoader, element: <NotaView /> },
  // Un solo segmento: la última, para que nunca le gane a una estática.
  { path: '/:categoria', key: CATEGORIA_ROUTE_KEY, loader: categoriaLoader, element: <CategoriaView /> },
];

/** Ruta que corresponde a una URL, o `null` si no hay match (→ 404). */
export function matchSsrRoute(
  pathname: string,
): { route: SsrRoute; params: Record<string, string | undefined> } | null {
  const matches = matchRoutes(
    ssrRoutes.map((r) => ({ path: r.path, id: r.key })),
    pathname,
  );
  const match = matches?.[matches.length - 1];
  if (!match) return null;
  const route = ssrRoutes.find((r) => r.key === match.route.id);
  if (!route) return null;
  return { route, params: match.params };
}
